import { useState, useEffect, useCallback } from "react"
import { toast } from "react-toastify"
import api from "../../../services/api"

const N_TAMANHO_PAGINA = 30
const N_ATRASO_DEBOUNCE = 300

// Hook de debounce genérico
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value)
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay)
    return () => clearTimeout(handler)
  }, [value, delay])
  return debouncedValue
}

/**
 * Hook para buscar contatos paginados.
 */
export const useBuscaContatos = (lAtivo, fnBuscarContatosSobrescrita = null, nCanalTipoId = null) => {
  const [cTermoBusca, setCTermoBusca] = useState("")
  const cTermoDebounced = useDebounce(cTermoBusca, N_ATRASO_DEBOUNCE)

  const [mContatos, setMContatos] = useState([])
  const [nPagina, setNPagina] = useState(1)
  const [lCarregando, setLCarregando] = useState(false)
  const [lTemMais, setLTemMais] = useState(true)

  useEffect(() => {
    if (!lAtivo) return
    setMContatos([])
    setNPagina(1)
    setLTemMais(true)
  }, [cTermoDebounced, lAtivo, nCanalTipoId])

  const fnBuscarDados = useCallback(async () => {
    if (!lAtivo || !lTemMais) return
    setLCarregando(true)

    try {
      let oDadosResposta = { contatos: [], temMais: false }

      if (typeof fnBuscarContatosSobrescrita === "function") {
        const oResposta = await fnBuscarContatosSobrescrita(cTermoDebounced, nPagina)
        oDadosResposta.contatos = Array.isArray(oResposta?.contatos) ? oResposta.contatos : []
        oDadosResposta.temMais =
          typeof oResposta?.hasMore === "boolean"
            ? oResposta.hasMore
            : oDadosResposta.contatos.length === N_TAMANHO_PAGINA
      } else {
        const { data } = await api.get("/contacts", {
          params: {
            filtro: cTermoDebounced || undefined,
            pagina: nPagina,
            ordenarPorUltimaMensagem: true,
            canalTipo: nCanalTipoId || undefined,
          },
        })
        oDadosResposta.contatos = Array.isArray(data?.contatos) ? data.contatos : []
        oDadosResposta.temMais =
          typeof data?.hasMore === "boolean" ? data.hasMore : oDadosResposta.contatos.length === N_TAMANHO_PAGINA
      }

      setMContatos((prev) => (nPagina === 1 ? oDadosResposta.contatos : [...prev, ...oDadosResposta.contatos]))
      setLTemMais(oDadosResposta.temMais)
    } catch (oErro) {
      console.error("Erro ao buscar contatos:", oErro)
      toast.error("Não foi possível carregar os contatos.")
      setLTemMais(false)
    } finally {
      setLCarregando(false)
    }
  }, [cTermoDebounced, nPagina, lAtivo, lTemMais, fnBuscarContatosSobrescrita, nCanalTipoId])

  useEffect(() => {
    fnBuscarDados()
  }, [fnBuscarDados])

  const fnCarregarMais = useCallback(() => {
    if (!lCarregando && lTemMais) setNPagina((p) => p + 1)
  }, [lCarregando, lTemMais])

  const fnReset = useCallback(() => {
    setMContatos([])
    setNPagina(1)
    setLTemMais(true)
    setCTermoBusca("")
  }, [])

  return {
    mContatos,
    cTermoBusca,
    lCarregando,
    lTemMais,
    fnSetTermoBusca: setCTermoBusca,
    fnCarregarMais,
    fnReset,
  }
}
